import type { StateCreator } from 'zustand';
import { CondoStore } from './shared';
import { EmailSchedule, BulkFeeRequest } from '../../types';
import { apiService } from '../../services/api';

export const createCommsSchedulingSlice: StateCreator<CondoStore, [], [], Partial<CondoStore>> = (set, get) => ({
  // ── Email Scheduling ──────────────────────────────────────────────────────

  fetchEmailSchedules: async (condoId: string) => {
    try {
      const schedules = await apiService.getEmailSchedules(condoId);
      set({ emailSchedules: schedules });
    } catch (error) {
      console.error('Failed to fetch email schedules:', error);
    }
  },

  createEmailSchedule: async (schedule: EmailSchedule) => {
    const created = await apiService.createEmailSchedule(schedule);
    set(state => ({ emailSchedules: [created, ...state.emailSchedules] }));
    return created;
  },

  updateEmailSchedule: async (id: string, schedule: Partial<EmailSchedule>) => {
    const updated = await apiService.updateEmailSchedule(id, schedule);
    set(state => ({
      emailSchedules: state.emailSchedules.map(s => s.id === id ? updated : s)
    }));
  },

  cancelEmailSchedule: async (id: string) => {
    await apiService.cancelEmailSchedule(id);
    set(state => ({
      emailSchedules: state.emailSchedules.map(s =>
        s.id === id ? { ...s, status: 'CANCELLED' as const } : s
      )
    }));
  },

  deleteEmailSchedule: async (id: string) => {
    await apiService.deleteEmailSchedule(id);
    set(state => ({
      emailSchedules: state.emailSchedules.filter(s => s.id !== id)
    }));
  },

  sendEmailNow: async (id: string) => {
    const updated = await apiService.sendEmailNow(id);
    set(state => ({
      emailSchedules: state.emailSchedules.map(s => s.id === id ? updated : s)
    }));
    return updated;
  },

  // ── Vendors (S7.1) ────────────────────────────────────────────────────────

  fetchVendors: async (condoId?: string) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const vendors = await apiService.getVendors(condoId);
      set({ vendors, error: null });
    } catch (error: any) {
      setError(error.message || 'Failed to fetch vendors');
    } finally {
      setLoading(false);
    }
  },

  createVendor: async (v) => {
    const created = await apiService.createVendor(v);
    set(state => ({ vendors: [created, ...state.vendors] }));
  },

  updateVendor: async (id, v) => {
    const updated = await apiService.updateVendor(id, v);
    set(state => ({ vendors: state.vendors.map(x => x.id === id ? updated : x) }));
  },

  deleteVendor: async (id) => {
    await apiService.deleteVendor(id);
    set(state => ({ vendors: state.vendors.filter(x => x.id !== id) }));
  },

  rateVendor: async (id, rating) => {
    const updated = await apiService.rateVendor(id, rating);
    set(state => ({ vendors: state.vendors.map(x => x.id === id ? updated : x) }));
  },

  // ── Announcements (S7.2) ──────────────────────────────────────────────────

  fetchAnnouncements: async (condoId?: string, publishedOnly = false) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const announcements = await apiService.getAnnouncements(condoId, publishedOnly);
      set({ announcements, error: null });
    } catch (error: any) {
      setError(error.message || 'Failed to fetch announcements');
    } finally {
      setLoading(false);
    }
  },

  createAnnouncement: async (a) => {
    const created = await apiService.createAnnouncement(a);
    set(state => ({ announcements: [created, ...state.announcements] }));
  },

  updateAnnouncement: async (id, a) => {
    const updated = await apiService.updateAnnouncement(id, a);
    set(state => ({ announcements: state.announcements.map(x => x.id === id ? updated : x) }));
  },

  archiveAnnouncement: async (id) => {
    const updated = await apiService.archiveAnnouncement(id);
    set(state => ({ announcements: state.announcements.map(x => x.id === id ? updated : x) }));
  },

  deleteAnnouncement: async (id) => {
    await apiService.deleteAnnouncement(id);
    set(state => ({ announcements: state.announcements.filter(x => x.id !== id) }));
  },

  // ── Late Fee Policy (S7.3) ────────────────────────────────────────────────

  fetchLateFeePolicy: async (condoId: string) => {
    try {
      const policy = await apiService.getLateFeePolicy(condoId);
      set({ lateFeePolicy: policy });
    } catch {
      set({ lateFeePolicy: null }); // 404 = no policy set yet
    }
  },

  upsertLateFeePolicy: async (condoId, policy) => {
    const saved = await apiService.upsertLateFeePolicy(condoId, policy);
    set({ lateFeePolicy: saved });
  },

  deleteLateFeePolicy: async (condoId) => {
    await apiService.deleteLateFeePolicy(condoId);
    set({ lateFeePolicy: null });
  },

  // ── Bulk Fees (S8.1) ──────────────────────────────────────────────────────

  bulkCreateFees: async (req: BulkFeeRequest) => {
    const { setLoading, setError, fetchFees } = get();
    setLoading(true);
    try {
      await apiService.bulkCreateFees(req);
      await fetchFees(req.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to generate fees');
    } finally {
      setLoading(false);
    }
  },

  // ── Unit Transfers (S8.2) ─────────────────────────────────────────────────

  fetchUnitTransfers: async (condoId?: string) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const unitTransfers = await apiService.getUnitTransfers(condoId);
      set({ unitTransfers, error: null });
    } catch (error: any) {
      setError(error.message || 'Failed to fetch unit transfers');
    } finally {
      setLoading(false);
    }
  },

  recordUnitTransfer: async (transfer) => {
    const created = await apiService.recordUnitTransfer(transfer);
    set(state => ({ unitTransfers: [created, ...state.unitTransfers] }));
    return created;
  },

  // ── Access Keys (S8.3) ────────────────────────────────────────────────────

  fetchAccessKeys: async (condoId?: string) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const accessKeys = await apiService.getAccessKeys(condoId);
      set({ accessKeys, error: null });
    } catch (error: any) {
      setError(error.message || 'Failed to fetch access keys');
    } finally {
      setLoading(false);
    }
  },

  createAccessKey: async (key) => {
    const created = await apiService.createAccessKey(key);
    set(state => ({ accessKeys: [created, ...state.accessKeys] }));
  },

  updateAccessKey: async (id, key) => {
    const updated = await apiService.updateAccessKey(id, key);
    set(state => ({ accessKeys: state.accessKeys.map(k => k.id === id ? updated : k) }));
  },

  returnAccessKey: async (id) => {
    const updated = await apiService.returnAccessKey(id);
    set(state => ({ accessKeys: state.accessKeys.map(k => k.id === id ? updated : k) }));
  },

  reportAccessKeyLost: async (id) => {
    const updated = await apiService.reportAccessKeyLost(id);
    set(state => ({ accessKeys: state.accessKeys.map(k => k.id === id ? updated : k) }));
  },

  deleteAccessKey: async (id) => {
    await apiService.deleteAccessKey(id);
    set(state => ({ accessKeys: state.accessKeys.filter(k => k.id !== id) }));
  },

  // ── Owner Notifications (S9.1) ────────────────────────────────────────────

  fetchNotifications: async (condoId, activeOnly = true) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const notifications = await apiService.getNotifications(condoId, activeOnly);
      set({ notifications, error: null });
    } catch (error: any) {
      setError(error.message || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  },

  createNotification: async (n) => {
    const created = await apiService.createNotification(n);
    set(state => ({ notifications: [created, ...state.notifications] }));
  },

  updateNotification: async (id, n) => {
    const updated = await apiService.updateNotification(id, n);
    set(state => ({ notifications: state.notifications.map(x => x.id === id ? updated : x) }));
  },

  deactivateNotification: async (id) => {
    const updated = await apiService.deactivateNotification(id);
    set(state => ({ notifications: state.notifications.map(x => x.id === id ? updated : x) }));
  },

  deleteNotification: async (id) => {
    await apiService.deleteNotification(id);
    set(state => ({ notifications: state.notifications.filter(x => x.id !== id) }));
  },

  // ── Maintenance Schedules (S9.2) ──────────────────────────────────────────

  fetchMaintenanceSchedules: async (condoId) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const maintenanceSchedules = await apiService.getMaintenanceSchedules(condoId);
      set({ maintenanceSchedules, error: null });
    } catch (error: any) {
      setError(error.message || 'Failed to fetch maintenance schedules');
    } finally {
      setLoading(false);
    }
  },

  createMaintenanceSchedule: async (ms) => {
    const created = await apiService.createMaintenanceSchedule(ms);
    set(state => ({ maintenanceSchedules: [...state.maintenanceSchedules, created] }));
  },

  updateMaintenanceSchedule: async (id, ms) => {
    const updated = await apiService.updateMaintenanceSchedule(id, ms);
    set(state => ({ maintenanceSchedules: state.maintenanceSchedules.map(x => x.id === id ? updated : x) }));
  },

  completeMaintenanceSchedule: async (id) => {
    const updated = await apiService.completeMaintenanceSchedule(id);
    set(state => ({ maintenanceSchedules: state.maintenanceSchedules.map(x => x.id === id ? updated : x) }));
  },

  setMaintenanceScheduleStatus: async (id, status) => {
    const updated = await apiService.setMaintenanceScheduleStatus(id, status);
    set(state => ({ maintenanceSchedules: state.maintenanceSchedules.map(x => x.id === id ? updated : x) }));
  },

  deleteMaintenanceSchedule: async (id) => {
    await apiService.deleteMaintenanceSchedule(id);
    set(state => ({ maintenanceSchedules: state.maintenanceSchedules.filter(x => x.id !== id) }));
  },

  // ── Violation Fine Management (S9.3) ─────────────────────────────────────

  applyViolationFine: async (id, amount) => {
    const updated = await apiService.applyViolationFine(id, amount);
    set(state => ({ violations: state.violations.map((v: any) => v.id === id ? updated : v) }));
  },

  markViolationFinePaid: async (id) => {
    const updated = await apiService.markViolationFinePaid(id);
    set(state => ({ violations: state.violations.map((v: any) => v.id === id ? updated : v) }));
  },

  // ── Work Order Vendor/Cost (S9.4) ─────────────────────────────────────────

  assignWorkOrderVendor: async (id, vendorId, vendorName, vendorContact) => {
    const updated = await apiService.assignWorkOrderVendor(id, vendorId, vendorName, vendorContact);
    set(state => ({ workOrders: state.workOrders.map((w: any) => w.id === id ? updated : w) }));
  },

  signOffWorkOrder: async (id, signedOffBy) => {
    const updated = await apiService.signOffWorkOrder(id, signedOffBy);
    set(state => ({ workOrders: state.workOrders.map((w: any) => w.id === id ? updated : w) }));
  },

  updateWorkOrderCosts: async (id, laborCost, materialCost, invoiceNumber) => {
    const updated = await apiService.updateWorkOrderCosts(id, laborCost, materialCost, invoiceNumber);
    set(state => ({ workOrders: state.workOrders.map((w: any) => w.id === id ? updated : w) }));
  },
});
